// src/pages/ProposalView.jsx
// Page PUBLIQUE d'une proposition d'acquisition (lien par jeton, sans compte).
// Le client parcourt les œuvres, en compare 2 ou 3, réagit, puis envoie ses réponses.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import BrandMark from "@/components/BrandMark";
import { callProposal, REACTION_LABELS } from "@/lib/proposalApi";

const money = (n, cur) =>
  n == null ? "Prix sur demande" : `${Number(n).toLocaleString("fr-FR")} ${cur === "EUR" || !cur ? "€" : cur}`;

const REACTION_STYLE = {
  interested: { on: "bg-[#1a1a1a] text-[#f9f7f4] border-[#1a1a1a]" },
  not_for_me: { on: "bg-[#6b6459] text-white border-[#6b6459]" },
  question: { on: "bg-[#c9a961] text-[#1a1a1a] border-[#c9a961]" },
};

function ReactionBar({ item, reaction, disabled, onSave }) {
  const [comment, setComment] = useState(reaction?.comment || "");
  useEffect(() => setComment(reaction?.comment || ""), [reaction?.comment, item.id]);
  return (
    <div className="mt-4">
      <div className="flex flex-wrap gap-2">
        {Object.entries(REACTION_LABELS).map(([key, label]) => {
          const active = reaction?.reaction === key;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => onSave(item.id, key, comment)}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                active ? REACTION_STYLE[key].on : "border-[#e5e0d8] text-[#4a453d] hover:bg-[#f9f7f4]"
              } disabled:opacity-60`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {reaction && (
        <textarea
          value={comment}
          disabled={disabled}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => comment !== (reaction?.comment || "") && onSave(item.id, reaction.reaction, comment)}
          placeholder="Un commentaire pour votre conseiller (facultatif)"
          rows={2}
          className="mt-3 w-full px-3 py-2 border border-[#e5e0d8] rounded-lg text-sm bg-white"
        />
      )}
    </div>
  );
}

function ItemImage({ item, className = "" }) {
  return item.image_url ? (
    <img src={item.image_url} alt={item.title || ""} className={`w-full object-contain bg-[#f3f0ea] ${className}`} />
  ) : (
    <div className={`w-full bg-[#f3f0ea] flex items-center justify-center text-[#b9b2a4] text-sm ${className}`}>
      Pas d'image
    </div>
  );
}

export default function ProposalView() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [reactions, setReactions] = useState({});
  const [mode, setMode] = useState("browse");
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    document.title = "Proposition d'acquisition";
    let alive = true;
    callProposal({ token })
      .then((data) => {
        if (!alive) return;
        setState({ loading: false, error: null, data });
        setReactions(data.reactions || {});
      })
      .catch((e) => alive && setState({ loading: false, error: e.message, data: null }));
    return () => { alive = false; };
  }, [token]);

  const items = useMemo(() => state.data?.items || [], [state.data]);
  const submitted = Boolean(state.data?.proposal?.submitted_at);
  const answered = Object.keys(reactions).length;

  const save = useCallback(async (artworkId, reaction, comment) => {
    setReactions((r) => ({ ...r, [artworkId]: { reaction, comment } }));
    try {
      await callProposal({ token, action: "react", artwork_id: artworkId, reaction, comment });
      setNotice(null);
    } catch (e) {
      setNotice(e.message);
    }
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      await callProposal({ token, action: "submit" });
      setState((s) => ({ ...s, data: { ...s.data, proposal: { ...s.data.proposal, submitted_at: new Date().toISOString() } } }));
      setNotice(null);
    } catch (e) {
      setNotice(e.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 3 ? p : [...p, id]));

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f9f7f4] text-[#6b6459] font-serif">Chargement…</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f7f4] p-6 text-center font-serif">
        <div>
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <p className="mt-6 text-lg text-[#2a261f]">{state.error}</p>
          <p className="mt-2 text-sm text-[#8a8378]">Demandez un nouveau lien à votre conseiller.</p>
        </div>
      </div>
    );
  }

  const { proposal } = state.data;
  const current = items[index];
  const compared = items.filter((i) => picked.includes(i.id));
  const expires = new Date(proposal.expires_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <header className="border-b border-[#e5e0d8] bg-[#f9f7f4]/95">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <span className="text-xs text-[#8a8378]">Lien valable jusqu'au {expires}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <p className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Proposition d'acquisition</p>
        <h1 className="text-3xl md:text-4xl font-bold">{proposal.title}</h1>
        <p className="mt-2 text-[#4a453d]">
          {proposal.advisor_name ? `Sélectionnée par ${proposal.advisor_name}` : "Sélection de votre conseiller"}
          {proposal.client_label ? ` pour ${proposal.client_label}` : ""}.
        </p>
        <p className="mt-4 text-sm text-[#6b6459] border-l-2 border-[#c9a961] pl-4 max-w-3xl">
          Ces œuvres sont <strong>proposées par votre conseiller et ne sont pas détenues</strong>. Le prix indiqué est le prix
          demandé. Votre réaction n'engage à rien ; la suite se décide avec votre conseiller.
        </p>

        {submitted && (
          <div className="mt-8 bg-[#eef5f1] border border-[#cfe3d8] rounded-xl p-6">
            <div className="text-xl">✓ Vos réponses ont été envoyées à votre conseiller.</div>
            <p className="text-sm text-[#4a453d] mt-1">Merci. Vous pouvez encore consulter la proposition jusqu'au {expires}.</p>
          </div>
        )}
        {notice && <p className="mt-6 text-sm text-[#dc2626]">{notice}</p>}

        {items.length === 0 ? (
          <p className="mt-10 text-[#6b6459]">Cette proposition ne contient pas encore d'œuvre.</p>
        ) : (
          <>
            <div className="mt-8 flex gap-2 border-b border-[#e5e0d8]">
              {[["browse", "Œuvre par œuvre"], ["compare", "Comparer"]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setMode(k)}
                  className={`px-4 py-2 text-sm -mb-px border-b-2 ${mode === k ? "border-[#1a1a1a] font-semibold" : "border-transparent text-[#6b6459]"}`}
                >
                  {l}
                </button>
              ))}
            </div>

            {mode === "browse" && current && (
              <section className="mt-8 grid md:grid-cols-2 gap-8">
                <div>
                  <ItemImage item={current} className="max-h-[520px] min-h-[280px]" />
                  <div className="mt-3 flex items-center justify-between text-sm text-[#6b6459]">
                    <button disabled={index === 0} onClick={() => setIndex(index - 1)} className="px-3 py-1.5 border border-[#e5e0d8] rounded-lg disabled:opacity-40">← Précédente</button>
                    <span>{index + 1} / {items.length}</span>
                    <button disabled={index === items.length - 1} onClick={() => setIndex(index + 1)} className="px-3 py-1.5 border border-[#e5e0d8] rounded-lg disabled:opacity-40">Suivante →</button>
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{current.title}</h2>
                  <p className="text-lg text-[#4a453d]">{current.artist}{current.year ? `, ${current.year}` : ""}</p>
                  <p className="mt-2 text-sm text-[#6b6459]">{[current.medium, current.dimensions].filter(Boolean).join(" · ")}</p>
                  <p className="mt-4 text-2xl">{money(current.asking_price, current.currency)}</p>
                  <p className="text-xs text-[#8a8378]">Prix demandé</p>
                  {current.rationale && (
                    <div className="mt-5">
                      <div className="text-xs tracking-[0.14em] uppercase text-[#8a8378] mb-1">Pourquoi cette œuvre</div>
                      <p className="text-[15px] leading-relaxed text-[#2a261f] whitespace-pre-wrap">{current.rationale}</p>
                    </div>
                  )}
                  <ReactionBar item={current} reaction={reactions[current.id]} disabled={submitted} onSave={save} />
                  <label className="mt-6 inline-flex items-center gap-2 text-sm text-[#4a453d] cursor-pointer">
                    <input type="checkbox" checked={picked.includes(current.id)} onChange={() => togglePick(current.id)} />
                    Ajouter à la comparaison {picked.length >= 3 && !picked.includes(current.id) ? "(3 maximum)" : ""}
                  </label>
                </div>
              </section>
            )}

            {mode === "compare" && (
              <section className="mt-8">
                {compared.length < 2 ? (
                  <div>
                    <p className="text-[#4a453d]">Choisissez 2 ou 3 œuvres à comparer.</p>
                    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {items.map((i) => (
                        <label key={i.id} className={`bg-white border rounded-xl p-3 cursor-pointer ${picked.includes(i.id) ? "border-[#1a1a1a]" : "border-[#e5e0d8]"}`}>
                          <ItemImage item={i} className="h-40" />
                          <div className="mt-2 flex items-start gap-2 text-sm">
                            <input type="checkbox" className="mt-1" checked={picked.includes(i.id)} onChange={() => togglePick(i.id)} />
                            <span><strong>{i.title}</strong><br />{i.artist}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="grid gap-5" style={{ gridTemplateColumns: `repeat(${compared.length}, minmax(240px, 1fr))` }}>
                      {compared.map((i) => (
                        <div key={i.id} className="bg-white border border-[#e5e0d8] rounded-2xl p-4 flex flex-col">
                          <ItemImage item={i} className="h-56 rounded-lg" />
                          <h3 className="mt-3 text-lg font-bold">{i.title}</h3>
                          <p className="text-[#4a453d]">{i.artist}{i.year ? `, ${i.year}` : ""}</p>
                          <dl className="mt-3 text-sm space-y-1 text-[#4a453d]">
                            <div><dt className="inline text-[#8a8378]">Technique : </dt><dd className="inline">{i.medium || "—"}</dd></div>
                            <div><dt className="inline text-[#8a8378]">Dimensions : </dt><dd className="inline">{i.dimensions || "—"}</dd></div>
                          </dl>
                          <p className="mt-3 text-xl">{money(i.asking_price, i.currency)}</p>
                          <p className="text-xs text-[#8a8378]">Prix demandé</p>
                          {i.rationale && <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">{i.rationale}</p>}
                          <ReactionBar item={i} reaction={reactions[i.id]} disabled={submitted} onSave={save} />
                          <button onClick={() => togglePick(i.id)} className="mt-4 text-xs text-[#8a8378] underline self-start">Retirer de la comparaison</button>
                        </div>
                      ))}
                    </div>
                    {compared.length < 3 && <p className="mt-4 text-sm text-[#8a8378]">Vous pouvez ajouter une troisième œuvre depuis « Œuvre par œuvre ».</p>}
                  </div>
                )}
              </section>
            )}

            {!submitted && (
              <div className="mt-12 border-t border-[#e5e0d8] pt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-[#6b6459]">{answered} œuvre(s) sur {items.length} avec une réponse.</p>
                <button
                  onClick={submit}
                  disabled={busy || answered === 0}
                  className="px-6 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#b9975a] disabled:opacity-50"
                >
                  {busy ? "Envoi…" : "Envoyer mes réponses à mon conseiller"}
                </button>
              </div>
            )}
          </>
        )}

        <p className="mt-12 text-xs text-[#8a8378] max-w-3xl">
          Les informations sont fournies par votre conseiller. Aucune estimation n'est affichée ici. La transaction et le prix
          définitif se décident entre vous et votre conseiller, hors plateforme.
        </p>
      </main>
    </div>
  );
}
